import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { dhfApi } from '../../utils/dhfApi';
import { Diff } from './variantsUi';
import '../../styles/DocReview.css';
import '../../styles/Dhf.css';
import '../../styles/DhfVariants.css';

const RESULT = {
  written: { tone: 'good', text: 'Written · read back' },
  not_confirmed: { tone: 'warn', text: 'Not confirmed' },
  refused: { tone: 'bad', text: 'Not written' },
  skipped: { tone: 'neutral', text: 'Skipped' },
};

const plain = (h) => (h || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * Merge to Orcanos (mockup screen 5, D7): every difference between this branch and the Orcanos
 * document. Nothing is ticked by default — merge only what is true for the origin product; text
 * that belongs to this branch alone stays here. Each write is read back from its paragraph; a row
 * says Written only when Orcanos holds the branch's text.
 */
export default function DhfVariantMergePage() {
  const { setupId, vid } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState({});
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await dhfApi.mergePlan(setupId, vid));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [setupId, vid]);
  useEffect(() => { load(); }, [load]);

  const mergeable = (data?.rows || []).filter((r) => (r.action === 'update' && data.can_update) || (r.action === 'add' && data.can_add));
  const chosen = Object.keys(picked).filter((k) => picked[k]);

  const write = async () => {
    setBusy(true);
    setConfirm(false);
    try {
      const r = await dhfApi.merge(setupId, vid, chosen);
      const by = {};
      r.results.forEach((x) => { by[x.nid] = x; });
      setResults((old) => ({ ...old, ...by }));
      setPicked({});
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <div className="drv-page"><div className="drv-error" role="alert">{error}</div></div>;
  if (!data) return <div className="drv-page"><div className="drv-muted">Loading…</div></div>;
  const done = Object.values(results);

  return (
    <div className="drv-page">
      <div className="dhf-doc-bar">
        <div className="dhf-doc-bar-l">
          <Link to={`/dhf/${setupId}/variant/${vid}`}>← {data.variant.name}</Link>
          <strong>Merge to Orcanos</strong>
          <span className="drv-muted">{data.variant.name} → origin {data.doc.url ? <a className="dhf-key" href={data.doc.url} target="_blank" rel="noopener noreferrer">{data.doc.key} ↗</a> : data.doc.key}</span>
        </div>
        <div className="dhf-doc-bar-r">
          <span className="drv-muted">{chosen.length} selected</span>
          <button type="button" className="drv-btn drv-btn--primary" disabled={busy || !chosen.length} onClick={() => setConfirm(true)}>
            {busy ? 'Writing…' : `Write ${chosen.length || ''} to Orcanos`}
          </button>
        </div>
      </div>

      {error && <div className="drv-error" role="alert">{error}</div>}
      <div className="dhfv-banners">
        <div className="dhf-warn"><span><strong>This changes the Orcanos document every branch comes from.</strong> Merge only what is true for the
          origin product. Text that belongs to this branch alone (labels, local rules) stays here — leave it unticked.</span></div>
        <div className="dhf-note" style={{ marginTop: 0 }}>Each write is <strong>read back</strong> from Orcanos; a row shows <em>Written</em> only
          when it stuck. New items are created under their paragraph and then need their traces in the matrix.
          {!data.can_update && ' You may not edit items, so updates are not offered.'}
          {!data.can_add && ' You may not add items, so new items are not offered.'}</div>
      </div>

      {done.length > 0 && (
        <div className="dhf-result" role="status">
          {done.filter((x) => x.result === 'written').length} written · {done.filter((x) => x.result !== 'written').length} not written — see each row.
        </div>
      )}

      <div className="drv-card dhf-table-wrap">
        <table className="dhf-table dhfv-merge-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                <input type="checkbox" aria-label="Select all"
                       checked={mergeable.length > 0 && mergeable.every((r) => picked[r.nid])}
                       onChange={(e) => { const p = {}; if (e.target.checked) mergeable.forEach((r) => { p[r.nid] = true; }); setPicked(p); }} />
              </th>
              <th>Item</th><th>Change in the branch</th><th style={{ width: 200 }}>Write as</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => {
              const can = mergeable.includes(r);
              return (
                <tr key={r.nid} className={can ? '' : 'dhfv-merge-off'}>
                  <td><input type="checkbox" disabled={!can || busy} aria-label={`Select ${r.mine.name}`}
                             checked={!!picked[r.nid]} onChange={(e) => setPicked({ ...picked, [r.nid]: e.target.checked })} /></td>
                  <td>
                    {r.mine.key ? <span className="dhf-key">{r.mine.key}</span>
                      : <span className="dhfv-state dhfv-state--green">{r.kind === 'par' ? 'NEW §' : 'NEW'}</span>}
                    <div className="dhf-sub">{r.para || 'top level'}</div>
                  </td>
                  <td className="dhfv-merge-txt">
                    {r.action === 'update' ? (
                      <>
                        <div className="dhfv-cmp-name"><Diff segs={r.diff.name} /></div>
                        <div><Diff segs={r.diff.description} /></div>
                      </>
                    ) : r.action === 'never' ? <span className="drv-muted">Removed in the branch</span> : (
                      <><div className="dhfv-cmp-name">{r.mine.name}</div><div className="dhfv-cmp-desc">{plain(r.mine.description)}</div></>
                    )}
                  </td>
                  <td>
                    {r.action === 'update' && <span>Update the origin {r.kind === 'par' ? 'paragraph' : 'item'}</span>}
                    {r.action === 'add' && <span>Add as a new item under {r.para || 'the top level'}</span>}
                    {(r.action === 'blocked' || r.action === 'never') && <span className="dhfv-tiny">{r.reason}</span>}
                  </td>
                </tr>
              );
            })}
            {data.rows.length === 0 && <tr><td colSpan={4} className="drv-muted">Nothing to merge — this branch matches the Orcanos document.</td></tr>}
          </tbody>
        </table>
      </div>

      {done.length > 0 && (
        <div className="drv-card dhfv-results">
          <strong>Last write</strong>
          <ul>
            {done.map((x) => {
              const t = RESULT[x.result] || RESULT.skipped;
              return (
                <li key={x.nid}>
                  <span className={`dhf-chip dhf-chip--${t.tone}`}>{x.key ? `${x.key} · ` : ''}{t.text}</span>
                  {x.detail && <span className="drv-muted"> {x.detail}</span>}
                  {x.created && x.result === 'written' && <span className="drv-muted"> — created, no traces yet: add them in the matrix.</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className="drv-muted" style={{ fontSize: 12 }}>Merged so far from this branch: {data.merged.length}. Merged items match the origin again, so they leave this list.</p>

      {confirm && (
        <div className="dhf-confirm" role="dialog" aria-label="Confirm merge">
          <span>Write <strong>{chosen.length}</strong> change{chosen.length > 1 ? 's' : ''} into <strong>{data.doc.key}</strong> in Orcanos?
            This changes the origin document for every branch.</span>
          <button type="button" className="drv-btn dhf-confirm-go" onClick={write}>Write to Orcanos</button>
          <button type="button" className="drv-btn" onClick={() => setConfirm(false)}>Cancel</button>
        </div>
      )}
    </div>
  );
}
